import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Truck, Mail, Lock, User, Phone, Loader2, Building2, MapPin, 
  FileText, CheckCircle2, AlertCircle, ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Alert, AlertDescription } from "../components/ui/alert";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function RegisterCompanyPage() {
  const { settings: siteSettings } = useSiteSettings();
  const [step, setStep] = useState(1); // 1: user data, 2: company data, 3: verification
  const [loading, setLoading] = useState(false);
  const [checkingVat, setCheckingVat] = useState(false);
  const [vatError, setVatError] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  
  const [formData, setFormData] = useState({
    // User data
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    // Company data
    company_name: "",
    company_description: "",
    company_address: "",
    company_city: "",
    company_province: "",
    company_cap: "",
    company_phone: "",
    company_email: "",
    company_vat_number: ""
  });

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear VAT error when user edits the VAT field
    if (name === "company_vat_number") {
      setVatError("");
    }
  };

  // Step 1: Validate user data
  const handleStep1 = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    // Check if email already exists
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-verification`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: "company"
      });
      
      toast.success("Dati utente validati!");
      
      // In dev mode without SMTP, show the code in toast
      if (response.data.message.includes("Codice di verifica (solo per test)")) {
        toast.info(response.data.message, { duration: 10000 });
      }
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la validazione");
    } finally {
      setLoading(false);
    }
  };

  // Check VAT number
  const checkVatNumber = async () => {
    if (!formData.company_vat_number.trim()) {
      setVatError("Inserisci la Partita IVA");
      return false;
    }

    setCheckingVat(true);
    setVatError("");

    try {
      const response = await axios.post(`${API}/auth/check-vat?vat_number=${encodeURIComponent(formData.company_vat_number)}`);
      
      if (response.data.exists) {
        setVatError(response.data.message);
        return false;
      }
      return true;
    } catch (error) {
      setVatError("Errore durante la verifica della Partita IVA");
      return false;
    } finally {
      setCheckingVat(false);
    }
  };

  // Step 2: Validate company data and proceed to verification
  const handleStep2 = async (e) => {
    e.preventDefault();
    
    // Check VAT number first
    const vatOk = await checkVatNumber();
    if (!vatOk) return;

    setStep(3);
  };

  // Step 3: Complete registration
  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error("Inserisci il codice a 6 cifre");
      return;
    }

    setLoading(true);

    try {
      // First verify email
      await axios.post(`${API}/auth/verify-email`, {
        email: formData.email,
        code: verificationCode
      });

      // Then register company
      const response = await axios.post(`${API}/auth/register-company`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone || null,
        company_name: formData.company_name,
        company_description: formData.company_description || null,
        company_address: formData.company_address,
        company_city: formData.company_city,
        company_province: formData.company_province,
        company_cap: formData.company_cap,
        company_phone: formData.company_phone,
        company_email: formData.company_email,
        company_vat_number: formData.company_vat_number
      });
      
      // Store token and user data
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      toast.success("Registrazione completata! Benvenuto su Noleggio Furgoni.");
      
      navigate("/company", { replace: true });
      window.location.reload();
    } catch (error) {
      const detail = error.response?.data?.detail || "Errore durante la registrazione";
      
      // If email verification failed, user might need to re-enter code
      if (detail.includes("Codice")) {
        toast.error(detail);
      } else if (detail.includes("Partita IVA")) {
        // VAT was registered by someone else in the meantime
        setVatError(detail);
        setStep(2);
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-verification`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: "company"
      });
      
      toast.success("Nuovo codice inviato!");
      
      if (response.data.message.includes("Codice di verifica (solo per test)")) {
        toast.info(response.data.message, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'invio del codice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1601628828688-632f38a5a7d0?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Fleet of vans"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-background via-background/50 to-transparent" />
        
        {/* Benefits on image */}
        <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent text-white">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Perché registrarti come Azienda?
          </h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              30 giorni di prova gratuita
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              Gestisci la tua flotta di veicoli
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              Ricevi prenotazioni online
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              Dashboard completa per il business
            </li>
          </ul>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-12 xl:px-16 bg-background py-8 overflow-y-auto">
        <div className="mx-auto w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-6">
            {siteSettings.logo_url ? (
              <img src={siteSettings.logo_url} alt="Logo" className="h-12 object-contain" />
            ) : (
              <>
                <div className="bg-primary p-2">
                  <Truck className="h-6 w-6 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  NOLEGGIO<span className="text-primary">FURGONI</span>.IT
                </span>
              </>
            )}
          </Link>

          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`h-0.5 w-8 ${step > s ? "bg-primary" : "bg-muted"}`} />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <>
              <h1 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Dati del Titolare
              </h1>
              <p className="text-muted-foreground text-sm mb-6">
                Inserisci i dati personali del titolare dell'azienda
              </p>

              <form onSubmit={handleStep1} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="input-label">Nome Completo *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Mario Rossi"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="input-label">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="nome@azienda.it"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="input-label">Telefono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+39 123 456 7890"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="input-label">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="input-label">Conferma Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifica in corso...
                    </>
                  ) : (
                    "Continua"
                  )}
                </Button>
              </form>

              <p className="text-sm text-muted-foreground text-center mt-4">
                Hai già un account?{" "}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Accedi
                </Link>
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
              >
                <ArrowLeft className="h-4 w-4" />
                Torna indietro
              </button>

              <h1 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Dati dell'Azienda
              </h1>
              <p className="text-muted-foreground text-sm mb-6">
                Inserisci i dati della tua azienda
              </p>

              <form onSubmit={handleStep2} className="space-y-4">
                <div>
                  <Label htmlFor="company_name" className="input-label">Nome Azienda *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company_name"
                      name="company_name"
                      type="text"
                      placeholder="La Mia Azienda SRL"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company_vat_number" className="input-label">Partita IVA *</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company_vat_number"
                      name="company_vat_number"
                      type="text"
                      placeholder="IT12345678901"
                      value={formData.company_vat_number}
                      onChange={handleChange}
                      className={`pl-10 ${vatError ? "border-red-500" : ""}`}
                      required
                    />
                  </div>
                  {vatError && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{vatError}</AlertDescription>
                    </Alert>
                  )}
                </div>

                <div>
                  <Label htmlFor="company_email" className="input-label">Email Azienda *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company_email"
                      name="company_email"
                      type="email"
                      placeholder="info@azienda.it"
                      value={formData.company_email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company_phone" className="input-label">Telefono Azienda *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company_phone"
                      name="company_phone"
                      type="tel"
                      placeholder="+39 02 1234567"
                      value={formData.company_phone}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company_address" className="input-label">Indirizzo *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company_address"
                      name="company_address"
                      type="text"
                      placeholder="Via Roma, 1"
                      value={formData.company_address}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="company_city" className="input-label">Città *</Label>
                    <Input
                      id="company_city"
                      name="company_city"
                      type="text"
                      placeholder="Milano"
                      value={formData.company_city}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_province" className="input-label">Provincia *</Label>
                    <Input
                      id="company_province"
                      name="company_province"
                      type="text"
                      placeholder="MI"
                      value={formData.company_province}
                      onChange={handleChange}
                      maxLength={2}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_cap" className="input-label">CAP *</Label>
                    <Input
                      id="company_cap"
                      name="company_cap"
                      type="text"
                      placeholder="20100"
                      value={formData.company_cap}
                      onChange={handleChange}
                      maxLength={5}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company_description" className="input-label">Descrizione (opzionale)</Label>
                  <Textarea
                    id="company_description"
                    name="company_description"
                    placeholder="Breve descrizione della tua azienda..."
                    value={formData.company_description}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full btn-primary" disabled={loading || checkingVat}>
                  {checkingVat ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifica P.IVA...
                    </>
                  ) : loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    "Continua"
                  )}
                </Button>
              </form>
            </>
          )}

          {step === 3 && (
            <>
              <button
                onClick={() => setStep(2)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
              >
                <ArrowLeft className="h-4 w-4" />
                Torna indietro
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Verifica la tua email
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    Codice inviato a <span className="font-medium text-foreground">{formData.email}</span>
                  </p>
                </div>
              </div>

              <form onSubmit={handleVerifyAndRegister} className="space-y-6">
                <div>
                  <Label htmlFor="code" className="input-label">Codice di Verifica</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    maxLength={6}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Registrazione in corso...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Completa Registrazione
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Non hai ricevuto il codice?{" "}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-primary hover:underline font-medium"
                    disabled={loading}
                  >
                    Invia di nuovo
                  </button>
                </p>
              </form>
            </>
          )}

          <p className="text-xs text-muted-foreground text-center mt-6">
            Registrandoti accetti i nostri{" "}
            <Link to="#" className="underline hover:text-primary">Termini e Condizioni</Link>{" "}
            e la{" "}
            <Link to="#" className="underline hover:text-primary">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
